import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { X, Loader2, Edit, Check } from "lucide-react";
import { useSuperAdmin } from "../../context/SuperAdminContext"

const COLOR = "#3B82F6";
export default function Managelocation() {

  const { addLocation, adding, locationStatusChange, togglingId, fetchalllocation, savingEdit, editLocation } = useSuperAdmin();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [locationName, setLocationName] = useState("");

  // Edit state
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");


  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchLocations = useCallback(async () => {
    setLoading(true);
    try {

      const res = await fetchalllocation();

      if (res.data.success && Array.isArray(res.data.locations)) {
        setLocations(
          res.data.locations.map((item) => ({
            id: item._id,
            name: item.name,
            code: item.locationCode || null,
            isActive: item.isActive ?? true,
          }))
        );
      }
    } catch (err) {
      toast.error("Failed to load locations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);

  // ── Add ───────────────────────────────────────────────────────────────────
  const handleAdd = async () => {
    try {
      const res = await addLocation(locationName)
      if (res.data.success) {
        toast.success("Location added successfully");
        setLocationName("");
        setShowForm(false);
        await fetchLocations();
      } else {
        toast.error(res.data.message || "Failed to add");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add");
    }
  };

  // ── Toggle Active/Inactive ─────────────────────────────────────────────────
  const handleToggle = async (loc) => {
    try {
      const res = await locationStatusChange(loc);
      if (res.data.success) {
        toast.success(`Location marked as ${!loc.isActive ? "Active" : "Inactive"}`);
        setLocations((prev) =>
          prev.map((l) => l.id === loc.id ? { ...l, isActive: !l.isActive } : l)
        );
      } else {
        toast.error(res.data.message || "Failed to update status");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update status");
    }
  };

  // ── Edit ──────────────────────────────────────────────────────────────────
  const startEdit = (loc) => {
    setEditingId(loc.id);
    setEditValue(loc.name);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue("");
  };

  const handleSaveEdit = async (loc) => {
    try {

      const res = await editLocation(editValue, loc);
      console.log(res);
      if (res.data.success) {
        toast.success("Location updated successfully");
        setLocations((prev) =>
          prev.map((l) => l.id === loc.id ? { ...l, name: editValue } : l)
        );
        cancelEdit();
      } else {
        toast.error(res.data.message || "Failed to update");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update");
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="p-6" style={{ fontFamily: "'Segoe UI', sans-serif" }}>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Manage Locations</h2>
            <p className="text-s text-gray-400">
              {loading ? "Loading..." : `Total Location : ${locations.length} `}
            </p>
          </div>
        </div>
        <div className="flex gap-2">

          <button
            onClick={() => {
              setShowForm((v) => !v);
              setLocationName("");

            }}
            className="px-4 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 flex items-center justify-center gap-1"
          >
            <span>Add Location</span>
          </button>
        </div>
      </div>

      {/* Add Form */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-2">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                Add Location
              </h3>

              <X
                className="h-5 w-5 cursor-pointer text-gray-500 hover:text-gray-700"
                onClick={() => {
                  setShowForm(false)
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location
              </label>
            </div>
            <div className="flex gap-2">

              <input
                value={locationName}
                onChange={(e) => setLocationName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                onFocus={(e) => (e.target.style.boxShadow = `0 0 0 2px ${COLOR}40`)}
                onBlur={(e) => (e.target.style.boxShadow = "none")}
                placeholder="Enter Location Name"
                disabled={adding}
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md"
              />
            </div>
            <div className="my-2">
              <button
                onClick={handleAdd}
                disabled={adding}
                className="px-3 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 disabled:bg-indigo-300"
              >
                {adding ? <><Loader2 size={14} className="animate-spin" /> Saving...</> : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      {!loading && locations.length > 0 && (
        <div className="w-full">
          <div className="max-h-[70vh] overflow-y-auto overflow-x-auto relative">
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 bg-white shadow-md z-[50]">
                <tr className="bg-gray-100">
                  <th className="p-2 text-left">Location Name</th>
                  <th className="p-2 text-center">Status</th>
                  <th className="p-2 text-center">Edit</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="4" className="text-center p-4">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : locations.length > 0 ? (
                  locations.map((loc) => (
                    <tr
                      key={loc.id}
                      className="border-b hover:bg-gray-50"
                    >
                      {/* Location Name */}
                      <td className="p-2 text-left min-w-[400px]">
                        <div className="flex items-center gap-2">
                          {editingId === loc.id ? (
                            <div className="flex items-center gap-2 flex-1">
                              <input
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") handleSaveEdit(loc);
                                  if (e.key === "Escape") cancelEdit();
                                }}
                                autoFocus
                                className="flex-1 px-2 py-1 text-sm border border-blue-300 rounded-lg bg-white focus:outline-none"
                                style={{ boxShadow: `0 0 0 2px ${COLOR}30` }}
                              />
                              {savingEdit ? (
                                <Loader2 size={14} className="animate-spin text-blue-400" />
                              ) : (
                                <>
                                  <button
                                    onClick={() => handleSaveEdit(loc)}
                                    className="p-1 rounded-md bg-blue-50 hover:bg-blue-100 transition-all"
                                    title="Save"
                                  >
                                    <Check size={14} color={COLOR} />
                                  </button>
                                  <button
                                    onClick={cancelEdit}
                                    className="p-1 rounded-md bg-gray-100 hover:bg-gray-200 transition-all"
                                    title="Cancel"
                                  >
                                    <X size={14} color="#9CA3AF" />
                                  </button>
                                </>
                              )}
                            </div>
                          ) : (
                            <span className={`font-medium ${loc.isActive ? "text-gray-800" : "text-gray-400"}`}>
                              {loc.name} ({loc.code})
                            </span>
                          )}
                        </div>
                      </td>


                      {/* Status Toggle */}
                      <td className="p-2 text-center">
                        {togglingId === loc.id ? (
                          <Loader2 size={15} className="animate-spin text-gray-400 mx-auto" />
                        ) : (
                          <label className="inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={loc.isActive}
                              onChange={() => handleToggle(loc)}
                              className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                            />
                            <span className="ml-2 text-xs font-semibold">
                              {"Active"}
                            </span>
                          </label>
                        )}
                      </td>

                      {/* Edit Button */}
                      <td className="p-2 text-center">
                        {editingId !== loc.id && (
                          <button
                            onClick={() => startEdit(loc)}
                            className="p-1.5 rounded-lg hover:bg-blue-50 transition-all mx-auto block"
                            title="Edit location name"
                          >
                            <Edit size={14} color={COLOR} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center p-4 text-gray-500">
                      No locations found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!loading && locations.length === 0 && (
        <div className="text-center p-4 text-gray-500 text-sm">
          No locations yet.
        </div>
      )}

      {loading && (
        <div className="text-center p-4">
          <Loader2 className="h-6 w-6 animate-spin mx-auto" />
        </div>
      )}
    </div>
  );
}